import * as React from "react";
import {
  Button,
  Card,
  Input,
  MessageBar,
  MessageBarBody,
  Spinner,
  Subtitle1,
  Tab,
  TabList,
  Text,
} from "@fluentui/react-components";
import { Checkmark20Regular, Dismiss20Regular, ArrowUndo20Regular } from "@fluentui/react-icons";
import { Link } from "react-router";
import { apiFetch } from "../../utils/apiFetch";

/**
 * Einstellungen → „Kontakt-Freigaben" (Genehmigungen, 30.09.2026). admin und
 * gfgl sehen alle Anfragen; FO und BO (bzw. die Teamleiter des BO-Teams)
 * sehen und entscheiden seit 01.10.2026 die Anfragen zu IHREN Kontakten —
 * der Server filtert die Liste und prüft jede Entscheidung. Wer ohne
 * Schreibrecht auf das Schloss einer Akte klickt,
 * kann um Freigabe bitten; hier wird entschieden: genehmigen (optional mit
 * „gültig bis"), ablehnen oder eine erteilte Freigabe entziehen.
 */

export type FreigabeTab = "offen" | "genehmigt" | "abgeschlossen";

export interface FreigabeZeile {
  id: number;
  contactId: number;
  contactName: string;
  requesterUserId: string;
  requesterName: string;
  status: "PENDING" | "GRANTED" | "DENIED" | "REVOKED";
  reason: string | null;
  createdAt: string;
  decidedAt: string | null;
  decidedByName: string | null;
  validUntil: string | null;
  abgelaufen: boolean;
}

const TABS: { key: FreigabeTab; label: string }[] = [
  { key: "offen", label: "Offen" },
  { key: "genehmigt", label: "Genehmigt" },
  { key: "abgeschlossen", label: "Abgelehnt/Entzogen" },
];

const STATUS_LABEL: Record<FreigabeZeile["status"], string> = {
  PENDING: "offen",
  GRANTED: "genehmigt",
  DENIED: "abgelehnt",
  REVOKED: "entzogen",
};

const deDatum = (iso: string | null | undefined): string => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
};

/** `YYYY-MM-DD` aus dem Datumsfeld → Ende dieses Tages (lokal) als ISO. */
export function gueltigBisAusFeld(wert: string): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(wert.trim());
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 23, 59, 59);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

const zelle: React.CSSProperties = { padding: "8px 10px", verticalAlign: "top", borderBottom: "1px solid #eee" };

export const KontaktFreigabenSection: React.FC = () => {
  const [tab, setTab] = React.useState<FreigabeTab>("offen");
  const [rows, setRows] = React.useState<FreigabeZeile[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [busyId, setBusyId] = React.useState<number | null>(null);
  const [gueltigBis, setGueltigBis] = React.useState<Record<number, string>>({});

  const laden = React.useCallback(async (t: FreigabeTab) => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await apiFetch<FreigabeZeile[]>("rbac/contact-grants", { params: { status: t } });
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(String((e as { message?: string })?.message ?? e));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    void laden(tab);
  }, [tab, laden]);

  const entscheiden = async (z: FreigabeZeile, aktion: "approve" | "deny" | "revoke") => {
    setBusyId(z.id);
    setError(null);
    try {
      const bis = aktion === "approve" ? gueltigBisAusFeld(gueltigBis[z.id] ?? "") : null;
      await apiFetch(`rbac/contact-grants/${z.id}/${aktion}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(aktion === "approve" && bis ? { validUntil: bis } : {}),
      });
      // Die Zeile wandert in einen anderen Tab — lokal entfernen statt neu laden.
      setRows((alt) => alt.filter((r) => r.id !== z.id));
    } catch (e) {
      setError(String((e as { message?: string })?.message ?? e));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Card style={{ padding: 16, display: "flex", flexDirection: "column", gap: 12 }}>
      <div>
        <Subtitle1>Kontakt-Freigaben</Subtitle1>
        <Text as="p" size={200} style={{ margin: "4px 0 0" }}>
          Anfragen von Kolleginnen und Kollegen, einen Kontakt bearbeiten zu dürfen, für den sie
          nicht zuständig sind. Du siehst die Anfragen, über die du entscheiden darfst — als FO
          oder BO (Teamleitung) des Kontakts oder als Administration/Geschäftsleitung. Eine
          Freigabe gilt bis zum Entzug, auf Wunsch nur bis zu einem Datum.
        </Text>
      </div>

      <TabList
        selectedValue={tab}
        onTabSelect={(_e, d) => setTab(d.value as FreigabeTab)}
        aria-label="Freigaben filtern"
      >
        {TABS.map((t) => (
          <Tab key={t.key} value={t.key}>
            {t.label}
          </Tab>
        ))}
      </TabList>

      {error && (
        <MessageBar intent="error">
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}

      {loading ? (
        <Spinner size="small" label="Lade Freigaben…" />
      ) : rows.length === 0 ? (
        <Text>
          {tab === "offen"
            ? "Keine offenen Anfragen."
            : tab === "genehmigt"
              ? "Keine erteilten Freigaben."
              : "Keine abgelehnten oder entzogenen Freigaben."}
        </Text>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: "left" }}>
                <th style={zelle}>Kontakt</th>
                <th style={zelle}>Angefragt von</th>
                <th style={zelle}>Begründung</th>
                <th style={zelle}>Angefragt am</th>
                {tab !== "offen" && <th style={zelle}>Entschieden</th>}
                {tab === "genehmigt" && <th style={zelle}>Gültig bis</th>}
                {tab !== "abgeschlossen" && <th style={zelle}>Aktion</th>}
              </tr>
            </thead>
            <tbody>
              {rows.map((z) => (
                <tr key={z.id} data-testid={`freigabe-${z.id}`}>
                  <td style={zelle}>
                    <Link to={`/contacts/${z.contactId}`}>{z.contactName}</Link>
                  </td>
                  <td style={zelle}>{z.requesterName}</td>
                  <td style={{ ...zelle, whiteSpace: "pre-wrap", maxWidth: 360 }}>{z.reason || "—"}</td>
                  <td style={zelle}>{deDatum(z.createdAt)}</td>
                  {tab !== "offen" && (
                    <td style={zelle}>
                      {STATUS_LABEL[z.status]} am {deDatum(z.decidedAt)}
                      {z.decidedByName ? ` von ${z.decidedByName}` : ""}
                    </td>
                  )}
                  {tab === "genehmigt" && (
                    <td style={zelle}>
                      {z.validUntil ? deDatum(z.validUntil) : "bis zum Entzug"}
                      {z.abgelaufen ? " (abgelaufen)" : ""}
                    </td>
                  )}
                  {tab === "offen" && (
                    <td style={{ ...zelle, whiteSpace: "nowrap" }}>
                      <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                        <Input
                          type="date"
                          size="small"
                          aria-label={`Gültig bis (optional) für ${z.contactName}`}
                          value={gueltigBis[z.id] ?? ""}
                          onChange={(_e, d) => setGueltigBis((alt) => ({ ...alt, [z.id]: d.value }))}
                          disabled={busyId === z.id}
                        />
                        <Button
                          size="small"
                          appearance="primary"
                          icon={<Checkmark20Regular />}
                          disabled={busyId === z.id}
                          onClick={() => void entscheiden(z, "approve")}
                        >
                          Genehmigen
                        </Button>
                        <Button
                          size="small"
                          icon={<Dismiss20Regular />}
                          disabled={busyId === z.id}
                          onClick={() => void entscheiden(z, "deny")}
                        >
                          Ablehnen
                        </Button>
                      </div>
                    </td>
                  )}
                  {tab === "genehmigt" && (
                    <td style={zelle}>
                      <Button
                        size="small"
                        icon={<ArrowUndo20Regular />}
                        disabled={busyId === z.id}
                        onClick={() => void entscheiden(z, "revoke")}
                      >
                        Entziehen
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
};

export default KontaktFreigabenSection;
